/**
 * Low-level HTTP transport for the web app's API client.
 *
 * Builds the URL, attaches credentials, parses JSON, normalizes errors
 * to `Error & { status, code }` shape, and emits `vd:unauthorized` on
 * 401 so the auth provider can clear local state and route to sign-in.
 *
 * Pre-extraction this lived inline at the top of `api.ts`; moving it
 * out leaves api.ts as a clean per-resource method registry. The
 * resource methods all import `request` (or one of the convenience
 * verbs) from here.
 *
 * `API_BASE` is build-time-baked from `VITE_API_BASE` so production
 * deployments on a separate api subdomain (api.toado.dev) and same-
 * origin proxied deployments share one transport. The trailing
 * `/api` is appended at the fetch site; callers pass relative paths
 * like `/me`, `/tickets/${id}`.
 */

import { authSessionEpoch } from './authSessionEpoch';
import { pageAccount } from './pageAccount';
import { isWorkspaceWrite, tellExtensionWorkspaceChanged } from './extensionPinRelay';
import { markUnanswered } from '@vd/recorder-core/requestAnswer';

/** Build-time API origin. Empty default keeps the dev `/api` proxy working. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

/**
 * The error shape every consumer can rely on. The `code` is one of
 * `ApiErrorCode` from `@vd/types` when the api returns a structured
 * error body; absent otherwise.
 */
export type ApiError = Error & {
  status?: number;
  code?: string;
  retryAfterMs?: number;
  snapshot?: unknown;
  version?: number;
  /** On a recording part refused for a reservation: its last byte (TOADO-991). */
  reservedEnd?: number;
};

async function fetchResponse(path: string, init: RequestInit): Promise<Response> {
  // The account this page was built for (`pageAccount.ts`): another account's
  // session, from a sign-in in another tab, does not answer for it.
  const account = pageAccount();
  // One set of headers, in whatever shape the caller gave them (an object, a
  // Headers, pairs), with each default added only where the caller named
  // nothing, in any letter case. Two content types reached the server as
  // "application/json, application/json", which it refused as 415 (Jason,
  // 13 Oct); spreading the caller's into an object lost a Headers or pairs
  // (Codex review round 8).
  const headers = new Headers(init.headers);
  if (!headers.has('accept')) headers.set('Accept', 'application/json');
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) headers.set('Content-Type', 'application/json');
  if (account && !headers.has('x-toado-account')) headers.set('X-Toado-Account', account);
  // A fetch that never got a response says so, for a caller that must tell it from an answer (TOADO-995).
  const doFetch = () => fetch(`${API_BASE}/api${path}`, { credentials: 'include', ...init, headers })
    .catch((error: unknown) => { throw markUnanswered(error); });

  let res = await doFetch();

  // TOADO-9: a DO rolling deploy can 401 an in-flight request when it
  // lands on a container whose session store isn't hot yet. That 401
  // is transient: retried ONCE after a short delay, the request goes
  // through, rather than failing and setting the page asking who is
  // signed in (AuthProvider).
  //
  // Safe for every method, including mutations, on a rule the routes
  // keep rather than one this code can enforce: a route answers 401 from
  // its identity check, before it changes anything (requireAuth, or the
  // session read at the top of the /me routes). A route that wrote first
  // and then answered 401 would be applied twice. /auth/* is excluded - a
  // 401 from signin/refresh is a real credential answer, and retrying it
  // would only delay the error the caller needs.
  if (res.status === 401 && !path.startsWith('/auth/')) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    res = await doFetch();
  }
  return res;
}

/** `sentIn` is the auth epoch the request was sent in (`authSessionEpoch`). */
async function throwResponseError(res: Response, sentIn: number): Promise<never> {
  const ct = res.headers.get('content-type') ?? '';
  const payload = ct.includes('application/json') ? await res.json() : await res.text();
  const msg = (payload && typeof payload === 'object' && 'message' in payload)
    ? String((payload as { message: unknown }).message)
    : res.statusText;
  const err = new Error(msg) as ApiError;
  err.status = res.status;
  const retryAfter = res.headers.get('retry-after');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    const retryAt = Date.parse(retryAfter);
    const retryAfterMs = Number.isFinite(seconds)
      ? Math.max(0, seconds * 1_000)
      : Number.isFinite(retryAt) ? Math.max(0, retryAt - Date.now()) : undefined;
    if (retryAfterMs !== undefined) err.retryAfterMs = retryAfterMs;
  }
  if (payload && typeof payload === 'object' && 'code' in payload) {
    err.code = String((payload as { code: unknown }).code);
  }
  // The last byte a recording upload's reservation holds, on a part refused for it (TOADO-991).
  if (payload && typeof payload === 'object' && 'reservedEnd' in payload) {
    // A number as sent, not coerced: Number(null) would read as byte 0 (review round 5).
    const reservedEnd = (payload as { reservedEnd: unknown }).reservedEnd;
    if (typeof reservedEnd === 'number' && Number.isSafeInteger(reservedEnd) && reservedEnd >= 0) err.reservedEnd = reservedEnd;
  }
  if (payload && typeof payload === 'object' && 'snapshot' in payload) {
    err.snapshot = (payload as { snapshot: unknown }).snapshot;
  }
  if (payload && typeof payload === 'object' && 'version' in payload) {
    const version = Number((payload as { version: unknown }).version);
    if (Number.isSafeInteger(version) && version >= 0) err.version = version;
  }
  // A 401 ends the session the request was sent in, and only that one: one
  // answered after somebody has signed in on this page, or after another 401
  // has already ended it, says nothing about the session now.
  if (res.status === 401 && authSessionEpoch() === sentIn) {
    window.dispatchEvent(new CustomEvent('vd:unauthorized'));
  }
  throw err;
}

/** Successful raw response for protocols that carry state in headers. */
export async function requestRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const sentIn = authSessionEpoch();
  const res = await fetchResponse(path, init);
  if (!res.ok) return throwResponseError(res, sentIn);
  afterWrite(path, init);
  return res;
}

/**
 * A write that changed the people, projects, sites or ticket types the
 * extension keeps is told to it (TOADO-761, TOADO-832), here once rather than
 * at every call site, so a new settings screen cannot forget.
 */
function afterWrite(path: string, init: RequestInit): void {
  if (isWorkspaceWrite(init.method, path)) tellExtensionWorkspaceChanged();
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const sentIn = authSessionEpoch();
  const res = await fetchResponse(path, init);

  if (!res.ok) return throwResponseError(res, sentIn);
  afterWrite(path, init);
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const payload = ct.includes('application/json') ? await res.json() : await res.text();
  return payload as T;
}

/**
 * The account the session cookie holds now, or null when nobody is signed in.
 *
 * Asked without naming this page's account, so that another tab's sign-in
 * answers rather than being refused: it is how a page that was told 401 learns
 * whether its session ended or another account took the browser over, and the
 * two are handled differently (`AuthProvider`). It never ends a session itself.
 *
 * Only two 401s, the second after the half-second every request waits
 * (TOADO-9), say nobody is: what follows is clearing the browser of what the
 * session left. Anything else that is not one account's id, an answer that
 * stalls past `timeoutMs` included, throws, and the caller clears nothing on
 * a guess. Never from a cache.
 */
export async function whoIsSignedIn(timeoutMs = 10_000): Promise<string | null> {
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), timeoutMs);
  try {
    const ask = () => fetch(`${API_BASE}/api/me`, {
      credentials: 'include', cache: 'no-store', headers: { Accept: 'application/json' }, signal: deadline.signal,
    });
    let res = await ask();
    if (res.status === 401) {
      await new Promise((resolve, reject) => {
        const wait = setTimeout(resolve, 500);
        deadline.signal.addEventListener('abort', () => { clearTimeout(wait); reject(deadline.signal.reason); }, { once: true });
      });
      res = await ask();
    }
    if (res.status === 401) return null;
    if (!res.ok) throw new Error(`Could not tell who is signed in (${res.status}).`);
    const id = ((await res.json()) as { user?: { id?: unknown } } | null)?.user?.id;
    if (typeof id !== 'string' || !id) throw new Error('Could not tell who is signed in.');
    return id;
  } finally {
    clearTimeout(timer);
  }
}

export const get = <T>(path: string) => request<T>(path, { method: 'GET' });
export const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
export const patch = <T>(path: string, body?: unknown, init?: { signal?: AbortSignal }) =>
  request<T>(path, {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
    ...init,
  });
